<template>
  <nav-header :title="title"></nav-header>
  <main class="statistics-page">
    <div class="container statistics-container statistics-stack">
      <div class="statistics-note">
        <i class="material-icons" aria-hidden="true">schedule</i>
        <p>
        {{
          $englishPreferred
            ? "Data on this page is calculated nightly. The data can be up to one day old."
            : "Data fremvist på denne side udregnes hver nat, data kan derfor være op til et døgn bagud."
        }}
        </p>
      </div>

      <section class="statistics-panel">
        <div class="statistics-heading">
          <span class="statistics-heading-icon"><i class="material-icons">groups</i></span>
          <h2>{{ $englishPreferred ? "Participation and coverage" : "Deltagelse og dækning" }}</h2>
        </div>
        <div v-if="generalStatistics[0]" class="statistics-metric-grid">
          <article v-for="metric in projectMetrics" :key="metric.key" class="statistics-metric-card">
            <p>{{ metric.label }}</p>
            <strong>
              {{ generalStatistics[0][metric.key] }}<template v-if="metric.total"> / {{ metric.total }}</template>
            </strong>
          </article>
        </div>
        <div v-else class="statistics-empty-state">
          {{ $englishPreferred ? "Fetching project statistics" : "Henter projektstatistik" }}...
        </div>
      </section>

      <section class="statistics-panel">
        <div class="statistics-heading">
          <span class="statistics-heading-icon"><i class="material-icons">flutter_dash</i></span>
          <h2>{{ $englishPreferred ? "Most observed species" : "Mest indtastede arter" }}</h2>
        </div>
        <div class="statistics-species-grid">
          <article v-for="group in speciesGroups" :key="group.key" class="statistics-species-card">
            <h3>{{ group.label }} · top 5</h3>
            <ol>
              <li v-for="specie in group.species" :key="specie.id">
                {{ $englishPreferred ? specie.englishName : specie.danishName }}
              </li>
            </ol>
          </article>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import NavHeader from "@/components/navbars/NavHeader.vue";
import generalAtlasSquareStatisticRepository from "../idb/repositories/generalAtlasSquareStatisticRepository";
import specieRankingRepository from "../idb/repositories/SpecieRankingRepository";
import specieRepository from "../idb/repositories/specieRepository";
import axios from "axios";

export default {
  components: {
    NavHeader,
  },
  data() {
    return {
      title: this.$englishPreferred
        ? "General project statistics"
        : "Generel projektstatistik",
      speciesRankings: [],
      generalStatistics: [],
      species: [],
      totalSpecies: [],
      totalSpeciesCurrentYear: [],
      totalSpecies30Days: [],
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    projectMetrics() {
      return [
        {
          key: "totalUsersWithCount",
          label: this.$englishPreferred
            ? "Participants with at least one count · all time"
            : "Deltagere med mindst én tælling · i alt",
        },
        {
          key: "totalUsersWithCountCurrentYear",
          label: this.$englishPreferred
            ? `Participants with at least one count · ${this.currentYear}`
            : `Deltagere med mindst én tælling · ${this.currentYear}`,
        },
        {
          key: "totalUsersWithCount30Days",
          label: this.$englishPreferred
            ? "Participants with at least one count · recent 30 days"
            : "Deltagere med mindst én tælling · seneste 30 dage",
        },
        {
          key: "totalAtlasSquaresCounted",
          label: this.$englishPreferred ? "Covered squares · all time" : "Dækkede kvadrater · i alt",
          total: 2255,
        },
        {
          key: "totalAtlasSquaresCountedCurrentYear",
          label: this.$englishPreferred ? `Covered squares · ${this.currentYear}` : `Dækkede kvadrater · ${this.currentYear}`,
          total: 2255,
        },
        {
          key: "totalAtlasSquaresCounted30Days",
          label: this.$englishPreferred ? "Covered squares · recent 30 days" : "Dækkede kvadrater · seneste 30 dage",
          total: 2255,
        },
      ];
    },
    speciesGroups() {
      return [
        { key: "total", label: this.$englishPreferred ? "All time" : "I alt", species: this.totalSpecies },
        { key: "year", label: String(this.currentYear), species: this.totalSpeciesCurrentYear },
        { key: "recent", label: this.$englishPreferred ? "Recent 30 days" : "Seneste 30 dage", species: this.totalSpecies30Days },
      ];
    },
  },
  methods: {
    async getSpecieRankingsApi() {
      return axios.get("/spontan/statistics/general/species");
    },
    async getSpecieRankings() {
      await this.getSpecieRankingsApi()
        .then((response) => {
          this.speciesRankings = response.data;
        })
        .catch(() => {
          this.online = false;
        });
      await this.getSpecies();
      this.fillSpeciesRankings();
    },
    fillSpeciesRankings() {
      this.speciesRankings.forEach((speciesRanking) => {
        var totalResultSpecie = this.species.filter((specie) => {
          return specie.speciesNumber == speciesRanking.totalSpecieNumber;
        });
        if (totalResultSpecie.length > 0) {
          this.totalSpecies.push(totalResultSpecie[0]);
        }

        var totalResultSpecieCurrentYear = this.species.filter((specie) => {
          return (
            specie.speciesNumber == speciesRanking.totalCurrentYearSpecieNumber
          );
        });
        if (totalResultSpecieCurrentYear.length > 0) {
          this.totalSpeciesCurrentYear.push(totalResultSpecieCurrentYear[0]);
        }

        var totalResultSpecie30Days = this.species.filter((specie) => {
          return specie.speciesNumber == speciesRanking.total30DaysSpecieNumber;
        });
        if (totalResultSpecie30Days.length > 0) {
          this.totalSpecies30Days.push(totalResultSpecie30Days[0]);
        }
      });
    },
    async getGeneralAtlasSquareStatisticsApi() {
      return axios.get("/spontan/statistics/general");
    },
    async getGeneralAtlasSquareStatistics() {
      await this.getGeneralAtlasSquareStatisticsApi()
        .then((response) => {
          this.generalStatistics = response.data;
        })
        .catch(() => {
          this.online = false;
        });
    },
    async getSpecies() {
      this.species = await specieRepository.getSpecies();
    },
  },
  async mounted() {
    console.time("preparing data");
    // Attempt to retrieve data directly from API, otherwise use local
    this.getSpecieRankings();
    this.getGeneralAtlasSquareStatistics();

    console.timeEnd("preparing data");
  },
};
</script>

<style scoped>
:deep(#navbar) {
  margin-bottom: 0 !important;
}
</style>
